import {
	ADD_ANNOTATION,
	CLEAR_MAPS_STATE,
	DELETE_ANNOTATION,
	DELETED_SLIDE_ERROR,
	INIT_MAP_STATE,
	MAINTAIN_MAPS_STATE_FOR_SLIDE_IDS,
	PERMISSION_DENIED_ERROR,
	REMOVE_MAPS_STATE,
	SLIDE_NOT_FOUND_ERROR,
	UPDATE_ANNOTATION_STATE,
	UPDATE_BLOOD_ANNOTATION_STATE,
	UPDATE_DISPLAY_SETTING,
	UPDATE_FIELD_DISPLAY_SETTING,
	UPDATE_GRID_STATE,
	UPDATE_IHC_STATE,
	UPDATE_MAP_STATE,
	UPDATE_SELECTED_ANNOTATION_LIST,
	UPDATE_SLIDE
} from "../redux_constants/maps.state.const";
import {error_manager} from "../helper/error_manager";
import queryString from "query-string";
import querystring from "query-string";
import {filterAnnotationsInROI, isROIAnnotation} from "../component/gammaviewer/utils/annotations_app_utils";
import {delete_, get, get_with_creds, patch, post, post_response} from "../helper/request";
import {getStateAwareUrl, isOffline, notNull, uuid} from "../utils/utils";
import {updateActiveMap, updateDisplayedMap, updateLastMapCount, updateSync} from "./gamma.state.action";
import {expandAnnotation, selectAnnotation} from "./triggers.action";
import {store} from "../helper/store";
import {message} from "antd";
import {getBaseSlidePath, getSlideMeta, getSlideStitchManifest, getSlideTXTY} from "../utils/slide_utils";
import {cloudProvider, scanSpeed} from "utils/const";
import {setSlideToLoading, setSlideToNotLoading} from "./slideLoading.action";
import {notifySuccess} from "../component/gammaviewer/utils/display.notification";
import {markCaseSlideRead} from "./case.action";


// Naming convention for redux helper actions
export const _initMapState = (mapId, mapState) => ({
	type: INIT_MAP_STATE,
	mapId: mapId,
	mapState: mapState
});

export const initMapsState = (maps, fromUrl = false) => dispatch => {
    let fetchFirstMap = true;

    if (maps.length <= 1 || fromUrl) {
		dispatch(clearMapsState());
		dispatch(setSlideToLoading(maps[0].slideId))
	}
    else {
		let mapsState = store.getState().mapsStateReducer
		for(const mapId in mapsState){
			if(mapsState[mapId].slideId === maps[0].slideId){
				dispatch(updateActiveMap(mapId))
			}
		}
        dispatch(clearMapsStateNotForSlideIds([maps[0].slideId]))
        fetchFirstMap = false;
    }

    maps.length === 1 && dispatch(updateSync(false));

    maps.forEach((map, index) => {
        if (index === 0 && !fetchFirstMap && !fromUrl) {
            return;
        }
        let mapId = uuid();
        dispatch(_initMapState(mapId, map));
        if (index !== 0) {
            dispatch(initSlide(mapId, map.slideId, false));
        } else {
            dispatch(initSlide(mapId, map.slideId));
			if(maps.length === 1){
				dispatch(updateDisplayedMap(mapId))
			}
        }
        dispatch(updateLastMapCount())
    });
}

export const changeDisplayedSlide = (slide) => dispatch => {
	//get mapId for map in mapsState with slide id = slide and skip the activemap
	let mapId = Object.values(store.getState().mapsStateReducer).filter(map => map.id !== store.getState().gammaStateReducer.activeMapId && map.slideId === slide.id);
	dispatch(setSlideToLoading(slide.id))
	if (mapId.length === 0) {
		mapId = uuid();
		dispatch(_initMapState(mapId, { slideId: slide.id }));
		dispatch(initSlide(mapId, slide.id));
		dispatch(updateLastMapCount())
	} else {
		// If not initializing mapState again, the viewer gets stuck on old mapState (fix that bug to remove this)
		mapId = mapId[0].id;
		dispatch(_initMapState(mapId, { slideId: slide.id }));
		dispatch(initSlide(mapId, slide.id));
	}
	dispatch(updateDisplayedMap(mapId));
}

export const resetDisplayedSlide = () => dispatch => {
	//Remove all maps except the active map
	let mapsToRemove = Object.values(store.getState().mapsStateReducer).filter(map => map.id !== store.getState().gammaStateReducer.activeMapId);
	mapsToRemove.forEach(map => dispatch(removeMapsState(map.id)));
	//Reset displayed map to null
	dispatch(updateDisplayedMap(null));
}

export const clearMapsState = () => ({
	type: CLEAR_MAPS_STATE,
});

export const clearMapsStateNotForSlideIds = (slideIds) => ({
	type: MAINTAIN_MAPS_STATE_FOR_SLIDE_IDS,
	slideIds: slideIds
});

export const removeMapsState = (mapId) => ({
	type: REMOVE_MAPS_STATE,
	mapId: mapId
});


export const updateMapState = (mapId, mapState) => ({
	type: UPDATE_MAP_STATE,
	mapId: mapId,
	mapState: mapState
});

export const updateExposure = (mapId, exposure) => updateMapState(mapId, { exposure });


// Z Stack Actions
export const updateZStackLevel = (mapId, zStackLevel) => updateMapState(mapId, {
	zStackLevel: zStackLevel,
});


// Slide State Actions

export const _updateSlide = (mapId, slide) => ({
	type: UPDATE_SLIDE,
	mapId, slide,
})

const getRequiredSlideDataFromFiles = async (slide) => {

	const base_path = getBaseSlidePath(slide)
	const txtyPromise = getSlideTXTY(slide.smallest_tiled_level, base_path)
	const metaPromise = getSlideMeta(slide.scan_speed === scanSpeed.ZSTACK, base_path)
	const stitchManifestPromise = getSlideStitchManifest(slide, base_path)

	let exposure = 0;
	let gamma = 0;
	const cameraConfig = slide?.additional_configs;
	if (notNull(cameraConfig) && Object.keys(cameraConfig).length > 0 && cameraConfig.hasOwnProperty("gamma") &&
		cameraConfig.hasOwnProperty("exposureTime")) {
		exposure = cameraConfig.exposureTime > 0 && cameraConfig.exposureTime;
		gamma = cameraConfig.gamma > 0 && cameraConfig.gamma;
	}

	const [txty, meta, stitchManifest] = await Promise.all([txtyPromise, metaPromise, stitchManifestPromise])

	return {
		"txty": txty,
		"meta": meta,
		"gamma": gamma,
		"exposure": exposure,
		"stitchManifest": stitchManifest,
	}
}

/** Builds the manifest on the server, then loads it into the slide; resolves to true on success. */
export const buildSlideStitchManifest = (mapId, slide) => dispatch =>
	post(`/api/slide/${slide.id}/stitch_manifest/build/`, {})
		.then(() => getSlideStitchManifest(slide, getBaseSlidePath(slide), true))
		.then(stitchManifest => {
			if (!stitchManifest) {
				throw new Error("Stitch manifest was built but could not be loaded");
			}
			dispatch(_updateSlide(mapId, {stitchManifest}));
			message.success("Stitch manifest built");
			return true;
		})
		.catch(error => {
			error_manager("Error building stitch manifest", error);
			return false;
		});

const updateMapAndSlide = (mapId, slide, dispatch, resetActiveMapId) => {
	dispatch(updateMapState(mapId, { slide, slideId: slide.id }));
	// Fetching from frontend directly, to improve performance even more
	getRequiredSlideDataFromFiles(slide).then((data) => {
		dispatch(_updateSlide(mapId, data));
		if(resetActiveMapId){
			dispatch(updateActiveMap(mapId));
			dispatch(setSlideToNotLoading());
			notifySuccess("mapId", "Slide loaded successfully");
		}
    })
}

export const initSlide = (mapId, slideId, resetActiveMapId = true) => dispatch => {
	get(`/api/slide/${slideId}/`)
		.then(slide => {
			dispatch(markCaseSlideRead(slideId, slide.unread));

			// NOTE: it is important to let the signed cookies be set before calling updateMapAndSlide
			if (slide.cloud_provider === cloudProvider.AWS || isOffline()) {
				let remoteUrl = getStateAwareUrl('') || ''
				let url = `/api/set_signed_cookies/?slide_id=${slide.id}`;
				if (remoteUrl) {
					url += `&remote_url=${remoteUrl}`;
				}
				get_with_creds(url).then(function () {
					updateMapAndSlide(mapId, slide, dispatch, resetActiveMapId);
				});
				// let signedCookiesPromise = axios.get(url, { headers: { Authorization: AuthHeader() }, withCredentials: true });
				// signedCookiesPromise.then(function() {
				// 	updateMapAndSlide(mapId, slide, dispatch);
				// });
			} else {
				updateMapAndSlide(mapId, slide, dispatch, resetActiveMapId);
			}

			// Fetching data on disk asynchronously, to improve speed of loading on cloud
			// const additionalSlideInfoUrl = `/api/get_additional_slide_info_from_disk/?id=${slide.id}`;
			// get(additionalSlideInfoUrl)
			// 	.then(data => dispatch(_updateSlide(mapId, data)))
			// 	.catch(error => {
			// 		console.log("Error: ", error);
			// 	});

			dispatch(updateMapState(mapId, {displaySetting: slide.display_setting, is_dicom: slide.format_dicom}));
			dispatch(retrieveAnnotations(mapId, slide.id));
		})
		.catch(error => {
			if (error.response && error.response.status === 410) {
				error_manager('The slide you are trying to access has been deleted or does not exists', error)
                dispatch(deletedSlideError(mapId));
			} else if (error.response?.status === 404) {
                error_manager('The slide that you are trying to access does not exist', error)
                dispatch(slideNotFoundError(mapId));
			} else if (error.response && error.response.status === 403) {
                error_manager("You do not have access to this slide. Please ask your administrator to " +
                    "assign the case with slides to you or grant you access by sharing these slide with you.", error)
                dispatch(permissionDenied(mapId))
            } else {
				error_manager("Failed to initialise slide", error)
			}
		});
}

export const updateSlide = (mapId, slideId, slideData) => dispatch =>
	patch(`/api/slide/${slideId}/`, slideData)
		.then(slide => dispatch(_updateSlide(mapId, slide)))
		.catch(error => error_manager("Failed to update slide", error));

export const updateSlideSetting = (mapId, setting) => dispatch =>
	patch(`/api/slide/settings/${setting.id}/`, setting)
		.then(setting => dispatch(_updateSlide(mapId, { setting })))
		.catch(error => error_manager("Failed to update slide setting", error));

export const fullStitchSlide = (mapId, slideId) => dispatch => {
	post(`/api/stitch/full/`, queryString.stringify({ slideId: slideId }))
		.then(() => dispatch(initSlide(mapId, slideId)));
}

export const updateDigitalZoomStatus = (mapId, digitalZoomStatus) => updateMapState(mapId, {
	digitalZoomStatus: digitalZoomStatus,
});


// Grid App Actions
export const updateGridState = (mapId, gridState) => ({
	type: UPDATE_GRID_STATE,
	mapId: mapId,
	gridState: gridState,
});

export const updateGridStatus = (mapId, grid) => updateGridState(mapId, { grid });

export const updateCircleLayerStatus = (mapId, circleLayer) => updateGridState(mapId, { circleLayer });

export const updateGridColor = (mapId, color) => updateGridState(mapId, { color });

export const updateGridSpacing = (mapId, spacing) => updateGridState(mapId, { spacing });

export const updateGridWidth = (mapId, width) => updateGridState(mapId, { width });

export const updateGridFullExtent = (mapId, fullExtent) => updateGridState(mapId, { fullExtent });

export const updateGridDynamic = (mapId, dynamic) => updateGridState(mapId, { dynamic });

export const updateGridStepCord = (mapId, stepCord) => updateGridState(mapId, { stepCord });

export const deletedSlideError = (mapId) => ({ type: DELETED_SLIDE_ERROR, mapId });

export const slideNotFoundError = (mapId) => ({type: SLIDE_NOT_FOUND_ERROR, mapId});

export const permissionDenied = (mapId) => ({ type: PERMISSION_DENIED_ERROR, mapId });

// Display Setting App ACTIONS

export const loadDisplaySetting = (mapId, displaySettingId, slideId = "") => dispatch => {
	get(`/api/display/settings/${displaySettingId}/?slide_id=${slideId}`)
		.then(displaySetting => dispatch(updateMapState(mapId, { displaySetting })))
		.catch(error => error_manager("Failed to load display setting", error));
}

export const updateDisplaySetting = (mapId, displaySetting) => ({
	type: UPDATE_DISPLAY_SETTING, mapId, displaySetting,
});


// Field Display Setting App ACTIONS
export const loadFieldDisplaySetting = (mapId, displaySettingId) => dispatch => {
	get(`/api/display/settings/${displaySettingId}/`)
		.then(fieldDisplaySetting => dispatch(updateMapState(mapId, { fieldDisplaySetting })))
		.catch(error => error_manager("Failed to load display setting", error));
}

export const updateFieldDisplaySetting = (mapId, fieldDisplaySetting) => ({
	type: UPDATE_FIELD_DISPLAY_SETTING, mapId, fieldDisplaySetting,
});


// ANNOTATION APP ACTIONS
export const updateAnnotationState = (mapId, annotationState) => ({
	type: UPDATE_ANNOTATION_STATE,
	mapId: mapId,
	annotationState: annotationState,
});

export const updateSelectedAnnotationsList = (mapId, annotationIdList, isAddOperation) => ({
	type: UPDATE_SELECTED_ANNOTATION_LIST,
	mapId: mapId,
	annotationIdList: annotationIdList,
	isAddOperation: isAddOperation,
});

export const updateMarkedAnnotation = (mapId, annotation) => updateAnnotationState(mapId, {
	markedAnnotation: annotation,
});

// retrieveAnnotationsAppDataFromBackend
export const retrieveAnnotations = (mapId, slideId) => dispatch =>
	get(`/api/annotation/?slide=${slideId}`)
		.then(data => dispatch(updateAnnotationState(mapId, {
			annotations: filterAnnotationsInROI(data),
		})))
		.catch(error => {
			if (!error?.response?.status === 403) {
				error_manager("Annotation Retrieval Failed", error)
			}
			dispatch(updateAnnotationState(mapId, {
				annotations: filterAnnotationsInROI([]),
			}))
		});

export const updateAnnotationsBlood = (mode, mapId, slideId, url, urlParams, cb) => {
	post(url, querystring.stringify(urlParams)).then((res) => {
		store.dispatch(updateAnnotationState(mapId, {
			annotations: filterAnnotationsInROI(res['data']),
		}))
		cb(res);
	}).catch(error => error_manager("Annotation Retrieval Failed", error));
}

// [Priyanshu] - only add annotation in redux_reducers, (frontend), if annotation of same id exist, replace it
export const _addAnnotation = (mapId, annotation) => ({
	type: ADD_ANNOTATION,  // redux_reducers is made to facilitate extending the list
	mapId: mapId,
	annotation: annotation,
});

// addAnnotationToAnnotationApp  -> use getAnnotationFromPoints(color, area, perimeter, key, coord, center, creator,
// z, slide_id) before
export const addAnnotation = (mapId, annotation) => dispatch =>
	post(
		`/api/annotation/`, annotation)
		.then(data => isROIAnnotation(annotation) ? dispatch(retrieveAnnotations(mapId, annotation.slide)) :
			dispatch(_addAnnotation(mapId, data)))
		.catch(error => error_manager("Annotation Addition Failed", error));


export const useCropToBlendBackground = (mapId, slideId, cropDataURL) => dispatch => {
	// uses cropDataURL (based on user selected rectangular region) to change background color & re-stitch scan
	post_response(
		`/api/use_crop_to_blend_bg/`, { "imgBase64": cropDataURL, "slideId": slideId })
		.then(response => {
			([200, 201, 204, 301, 302].indexOf(response.status) !== -1 && response.data.success === true)
				? message.info("Blending Started in the background and may take up to 15 mins. You can savely navigate away.")
				: message.info("Failed to start blending. Contact Morphle Support")
		}
		)
		.catch(error => {
			error_manager("Failed to add annotation", error)
		});
}


export const updateAnnotation = (mapId, annotation) => dispatch =>
	patch(
		`/api/annotation/${annotation.id}/`, annotation)
		.then(data => {
			dispatch(_addAnnotation(mapId, data));
			dispatch(expandAnnotation(annotation.id));
			if (annotation.checked) {
				dispatch(selectAnnotation(annotation.id));
			}
		})
		.catch(error => error_manager("Failed to update annotation", error));

// [Priyanshu] - delete annotation from redux_reducers, (frontend)
export const _deleteAnnotations = (mapId, annotationId) => ({
	type: DELETE_ANNOTATION,
	mapId: mapId,
	annotationId: annotationId,
});

export const deleteAnnotation = (mapId, annotation) => dispatch =>
	delete_(`/api/annotation/${annotation.id}/`)
		.then(_ => isROIAnnotation(annotation) ?
			dispatch(retrieveAnnotations(mapId, annotation.slide)) :
			dispatch(_deleteAnnotations(mapId, annotation.id)))
		.catch(error => error_manager("Annotation Deletion Failed", error));

export const deleteMultipleAnnotations = (mapId, slideId, annotations) => dispatch => {
	const annotationIds = [];
	annotations.forEach(anno => annotationIds.push(anno.id))
	let value = { slide_id: slideId, anno_ids: annotationIds };
	post(`/api/delete_annotations/`, value)
		.then(_ => annotations.forEach(annotation => {
			isROIAnnotation(annotation) ?
				dispatch(retrieveAnnotations(mapId, annotation.slide)) :
				dispatch(_deleteAnnotations(mapId, annotation.id))
		}))
		.catch(error => error_manager("Annotation Deletion Failed", error));
}

//BLOOD ANNOTATIONS APP

export const retrieveBloodAnnotations = (mapId, slideId) => dispatch => {
	let url = `/api/get_all_field_view_annotations_wbc_gamma/`;
	let val = {
		slide_id: slideId,
		tag: '',
	}
	post_response(
		url, querystring.stringify(val))
		.then(response =>
			dispatch(updateBloodAnnotationState(mapId, {
				annotations: response.data.data,
			}))
		)
		.catch(error => error_manager("Failed to retrieve blood fields", error));
}

const hasbloodROIAnnotation = (annos, title) => {
	for (let i = 0; i < annos.length; i++) {
		if (annos[i].title === title)
			return annos[i].id;
	}
	return -1;
}

const patchROIAnnotation = (data, annotation, mapId) => dispatch => {
	annotation.title = "blood-roi";
	annotation.anno_drawer = 0;
	let resp = hasbloodROIAnnotation(data, annotation.title);
	if (resp === -1) {
		dispatch(addAnnotation(mapId, annotation));
	} else {
		annotation.id = resp;
		dispatch(updateAnnotation(mapId, annotation))
	}
	dispatch(retrieveBloodAnnotations(mapId, annotation.slide));

}


export const updateBloodAnnotationState = (mapId, bloodAnnotationState) => ({
	type: UPDATE_BLOOD_ANNOTATION_STATE,
	mapId: mapId,
	bloodAnnotationState: bloodAnnotationState,
});


// IHC APP ACTIONS
export const updateIHCState = (mapId, ihcState) => ({
	type: UPDATE_IHC_STATE,
	mapId: mapId,
	ihcState: ihcState,
});

export const updateSelectedIHCResult = (mapId, selectedIHCResult) => updateIHCState(mapId, {
	selectedIHCResult: selectedIHCResult,
});